import {
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Link,
  Paper,
  Skeleton,
  styled,
  Typography,
} from 'opensolar-ui'
import { useMemo } from 'react'
import {
  getDefaultContractTemplate,
  getDefaultContractTemplateForPaymentOption,
  getOverrideSelectValue,
  getSystemDisplayName,
} from '../../utils/contractTemplateUtils'
import { buildContractGroupingContext } from '../../utils/entitySharingUtils'
import type { ContractTemplateSettingsProps } from './types'
import PaymentOptionContractSelect from './PaymentOptionContractSelect'
import ProjectContractSelect from './ProjectContractSelect'

const Header = styled('div')(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '8px',
  },
  { name: 'ContractTemplateSettingsHeader' }
)

const ModeToggleLink = styled(Link)(
  {
    color: '#000 !important',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  { name: 'ContractTemplateSettingsModeToggle' }
)

const ContractPaper = styled(Paper)(
  {
    background: '#F8F8F8',
    padding: '12px',
    '@media (min-width: 960px)': {
      padding: '15px',
    },
  },
  { name: 'ContractTemplateSettingsContractPaper' }
)

const SystemTitle = styled('p')(
  {
    fontWeight: 600,
    margin: '0 0 8px',
  },
  { name: 'ContractTemplateSettingsSystemTitle' }
)

const ContractTemplateSettings = ({
  translate,
  disabled,
  contractsLoading,
  contracts,
  isPaymentOptionMode,
  onModeToggle,
  projectContractUrl,
  onProjectContractChange,
  systems,
  onPaymentOptionContractChange,
  currentOrgId,
  currentOrgUrl,
  projectOrgId,
  isSharedProject,
  onEditContract,
  selectMenuZIndex,
  sharedWith,
  currentOrg,
}: ContractTemplateSettingsProps) => {
  const defaultContractTemplate = useMemo(() => getDefaultContractTemplate(contracts), [contracts])

  const groupingContext = useMemo(
    () =>
      buildContractGroupingContext({
        currentOrgId,
        projectOrgId,
        currentOrg,
        sharedWith,
      }),
    [currentOrgId, projectOrgId, currentOrg, sharedWith]
  )

  const getDefaultContractLabel = (paymentOption: { id: number; title: string; payment_type: string }) => {
    const defTemplate = getDefaultContractTemplateForPaymentOption(contracts, paymentOption, defaultContractTemplate)
    return defTemplate ? `${translate('Default')} (${defTemplate.title})` : translate('Default Template')
  }

  const showContractsLoading = contractsLoading && !contracts?.length

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Header>
        <Typography textVariant="h6Bold">{translate('Contract Template')}</Typography>
        <ModeToggleLink
          component="button"
          type="button"
          underline="always"
          aria-disabled={disabled}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault()
              return
            }
            onModeToggle()
          }}
          sx={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        >
          {!isPaymentOptionMode ? translate('Set Per System/Payment option') : translate('Set Per Project')}
        </ModeToggleLink>
      </Header>
      <ContractPaper variant="outlined">
        {!isPaymentOptionMode ? (
          <ProjectContractSelect
            translate={translate}
            disabled={disabled}
            contractsLoading={showContractsLoading}
            contracts={contracts}
            projectContractUrl={projectContractUrl}
            onProjectContractChange={onProjectContractChange}
            currentOrgId={currentOrgId}
            currentOrgUrl={currentOrgUrl}
            projectOrgId={projectOrgId}
            isSharedProject={isSharedProject}
            onEditContract={onEditContract}
            groupingContext={groupingContext}
          />
        ) : showContractsLoading ? (
          <Skeleton width="100%" height={44} />
        ) : (
          systems.map((systemData) => (
            <div key={systemData.uuid}>
              <SystemTitle>
                {translate('System')}: {getSystemDisplayName(systemData, translate)}
              </SystemTitle>
              {systemData.payment_options.map((paymentOption) => (
                <PaymentOptionContractSelect
                  key={`${systemData.uuid}-${paymentOption.id}`}
                  disabled={disabled}
                  contracts={contracts}
                  paymentOption={paymentOption}
                  value={getOverrideSelectValue(systemData.contract_template_overrides, paymentOption.id)}
                  defaultContractLabel={getDefaultContractLabel(paymentOption)}
                  selectMenuZIndex={selectMenuZIndex}
                  translate={translate}
                  groupingContext={groupingContext}
                  onChange={(contractId) =>
                    onPaymentOptionContractChange(systemData.uuid, paymentOption.id, contractId)
                  }
                />
              ))}
            </div>
          ))
        )}
      </ContractPaper>
    </ComponentVersionsInherit>
  )
}

export default ContractTemplateSettings
